//NPM
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { map, isEmpty, find, filter, isNil, omitBy } from "lodash";
import nextbillion, { NBMap, Marker, Popup, DirectionsService } from "@nbai/nbmap-gl";

// ANTD
import { Col, Row, Checkbox, Steps, Radio, Divider } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";

// SHARED COMPONENTS
import { BasicModal, Button, Tabs, TextInput } from "@life-line-lab/components-react";

// HOOKS
import { useAccessControlList, useQuery, useSession } from "Hooks/index";

// STYLESHEETS
import "./control-tower.scss";
import { getRouteFromNbApi } from "Services/controlTower.services";
import { EditRouteModal } from "Components/delivery-management/parcel-tracking-modals/edit-route-modal";

const { roleTypes } = useSession();
const isRoleCustomer = roleTypes == "CUSTOMER-SUPER_ADMIN" && true;

interface MidMileCTProps {
  midMileData?: any;
  handleUpdateRoute?: any;
}

// ======================================================
//   Modal Handler
// ======================================================
const useModalHandler = () => {
  const initialModal = {
    open: false,
    type: "",
    record: {},
  };

  const [callModal, setCallModal] = useState<any>(<></>);
  const [modal, setModal] = useState<any>(initialModal);

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  return {
    modalObject: { setModal, setCallModal, handleResetModalState },
    modal,
    setModal,
    callModal,
    setCallModal,
    handleResetModalState,
  };
};

// ======================================================
//   Any functions for the main component
// ======================================================

const useMidMileView = ({ modalObject, handleUpdateRoute }) => {
  const { setCallModal, setModal, handleResetModalState } = modalObject || {};

  const handleOpenEditRoute = (info) => {
    const normalizedInfo = omitBy(
      {
        ...info,
        journeyCode: info?.trainPlanCode,
        trainLineData: info?.entireTrainLine,
        trainLine: info?.train?._id,
        trainStops: info?.train?.station?.map((stop) =>
          omitBy({ ...stop, name: null, coordinate: null }, isNil)
        ),
        entireTrainLine: null,
        trainPlanCode: null,
        plateNo: null,
        train: null,
        lastLocation: null,
      },
      isNil
    );

    setCallModal(
      <EditRouteModal
        handleCancel={handleResetModalState}
        handleUpdateRoute={handleUpdateRoute}
        info={normalizedInfo}
      />
    );
    setModal({ open: true });
  };

  return {
    handleOpenEditRoute,
  };
};

// ======================================================
//   Next Billion
// ======================================================

// contains map tracking information
const useNextBillion = ({ tasks }) => {
  const [selectedTasks, setSelectedTasks] = useState<any>({});

  useEffect(() => {
    nextbillion.apiKey = `${process.env.NEXT_BILLION_KEY}`;
    const nbmap = new NBMap({
      container: document.getElementById("map"),
      style: "https://api.nextbillion.io/maps/streets/style.json",
      zoom: 11,
      center: { lat: 3.164005474252092, lng: 101.65040013068355 },
    });

    let mapTasks: any = [];

    let filteredTasks = filter(tasks, (taskObj) => {
      return selectedTasks[taskObj.trainPlanCode];
    });
    if (!isEmpty(filteredTasks)) {
      mapTasks = filteredTasks;
    } else {
      return;
    }

    nbmap.on("load", async function () {
      // https://api.nextbillion.io/directions/json?key=b740d83af7ba471b962c9082b9a02db7&origin=3.0333444,101.695278&destination=3.123261,101.726961&steps=false&alternatives=false&departure_time=0&session=&geometry=polyline6&waypoints=3.123261,101.726962%7C3.123261,101.726964

      if (mapTasks.length) {
        map(mapTasks, async (taskObj) => {
          let filterStr = "";
          if (taskObj.train?.station.length) {
            let firstStation = taskObj.train?.station[0];
            let lastStation = taskObj.train?.station[taskObj.train?.station.length - 1];
            if (
              firstStation.coordinate?.lat > 90 ||
              firstStation.coordinate?.lat < -90 ||
              firstStation.coordinate?.long < -180 ||
              firstStation.coordinate?.long > 180
            ) {
              return;
            }
            filterStr += firstStation.coordinate
              ? `&origin=${firstStation.coordinate?.lat},${firstStation.coordinate?.lon}&destination=${lastStation.coordinate.lat},${lastStation.coordinate.lon}`
              : "";
          }
          const response = await getRouteFromNbApi(filterStr);

          /*return map(taskObj.train?.station, (stn,i)=> {
              if(stn?.location?.coordinate){
                // return addSimpleMarker(nbmap, tsk?.location?.coordinate?.lat , tsk?.location?.coordinate?.lon, tsk.orderCode)
              }
            })*/

          nbmap.map.addSource("route", {
            type: "geojson",
            data: {
              type: "Feature",
              properties: {},
              geometry: {
                type: "LineString",
                coordinates: nextbillion.utils.polyline
                  .decode(response.routes[0].geometry, 6)
                  .map((c) => c.reverse()),
              },
            },
          });
          nbmap.map.addLayer({
            id: "route",
            type: "line",
            source: "route",
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#0a7499",
              "line-width": 5,
            },
          });
        });
      }
      // const directionsService = new DirectionsService()
      // // request the directions api
      // directionsService
      //   .route({
      //     origin: { lat: 3.0333444, lng: 101.695278 },
      //     waypoints: [{ lat: 3.123261, lng: 101.726962 },{ lat: 3.123261, lng: 101.726964 }],
      //     destination: { lat: 3.123261, lng: 101.726961 }
      //   })
      //   .then((response)=>{
      // let response = {"status":"Ok","routes":[{"geometry":"{mbxDiq~}`E?i@@qz@dAaYTml@LiDdCkMhAwEnDgNlCiJ~EuIpFeIuE}I{D}GqBsCkKcT_H_MsF}JuMmOoBeAiGwAaFYiGKJ{z@U_TIkGYaKBgEb@iC`A{AvByAlBOrCCtBA~_@VwCuAyEiDeBcDcDsE_FkIsA_Eo@wCKkCQaDv@aNbA}_@cEAqCEsEMgJIcWQsDGuB?mB_BsB{Gj@o`@H{C^qQ`@sPDsAqTd@oNXw_@h@sJ?uKNyc@h@yKBeMD{DFeS\\}CR{Db@_Gn@sOdCaItA}E~@aKjBqCh@mATaC`@mJtAuGl@{CViBR{Ed@uFv@{BJc@BmENiEJ}BFsEHqDBkHNkBB_IH}@D{BFwB@mBAuECsIOcJIwFSG?sEOuCKaDS}Hc@sQ{A}Q{A{k@qEoH[eOXsQLoQLsCDqYl@aJPmIJuCBgHV{QE{a@GkGHmRRcRVe_@v@mMBeGF_ENmDL}C\\{Bl@cBvAoAvAm@tCOpH|@lYfC|k@I`NcA~MwBxHsD~FcGxGwJdH{EjDkGlDkErBoDz@qCP}@Ci@CcBQ_ASs@]s@Uw@_@{@cAe@mAYgDBaAFcBd@{AbAgCj@s@pAy@xAq@lBe@`JeAzBQ~AgAhCm@nB}@pCyBz@{@t@q@r@eAp@cA~@oB|AuDv@eFb@mGE}FOiFIsEOsUOaJGkOG{CIcHQcEMgDScDs@qEq@}BgAiC_BoCyCyCaBiAoBw@kCy@eE{@cC_@gD[uEQyFCkFo@gGeC}c@lAgKXoVb@gOZsFNwAD}BBmCFaf@z@mQJe^^}}@|@mJP_]`@qLVi]p@}U|@qIXqTl@cNXcTdAkXfAqUpAqKp@wHf@qBNqGb@_S`AmZtC{JdA}M|Ay@JqTpCqC^{C`@sT`Doo@hJcL~AeMjBogA|Oi]vEwCZuFn@mj@zH{QlCaDd@_D\\eLfA{CNyHr@yJlC{D|AgC~BwBjCsCvEcA~Cg@zDHtB\\`DhAvDtAhDpB|BlCzBdBv@zEfB`FnAdGz@bH^dCAzCSfCc@~B_A|BqAbByAlA{A\\m@t@qANc@f@{A`@_CHkAF{DUkE}AeFoAyB}A}By@eBg@}Am@uCa@sAcAcCi@iA{@gAaDwCsDyC}J}IiE}E_DkDiZe\\aIiIgBmBoE{EuCkD_L}MeBeByC_DeCwCsCcD_FiGaFyIkJmOaFcKqHaTgBqF_AuDq@kCe@cB_EqOuJ{^kGuV}BeIyAmGeB}Fe@eCkBgG_@qAgCcIwA{CoBiEcBqCeBcBoBwA_BeAiAo@oCiAsEsAiFeAoH_A_BMyIQoC?wD?yCH_G^iL~@uUxBkYhAyKZwSLeFAyELsFLiJD{GMgEOiJYuAGyEg@aDQ}_@}Cyh@{EqFw@eHyAsEcAoHqBaIgCeDyAqCoAgFwCwF{DaCaBqBsAi@e@qD{C{FiFeCaC}CaEg@q@yKyMoAuBmNsQ}JgM_@e@yDaFgD}CkDmDeCuC{I_NwAiCeAgBgD{GmJySy_@gz@eBkE}EkQgMcc@yNy^_GgNmC}FkKeTqCkF}BoE]k@mBaD{CaF]m@wEmHyDwFsIiM[c@aBgCuKmPeEeHS]cD{Fq@mAuBcEiCkFeJ{RqEqJe@gAiJqSeDsHcGiMmC}FoHePmEiKoBaFiAoCoAyC_CwGkFsLw@iC{BoGsAqFyAcHeAsGkAuMQ}EWgJYqQeAcd@@cJC_ACuAIcC]aJEyBs@kJq@yGo@gF[uCiDcZkG_i@gFyd@{AmM}Eec@kC{Se@{DuDa\\_CwQ{@eGwCiNc@kBWaAS{@aAeD}AmEkEmLoDyHeBiDm@qAuAeCmIoLaBuByBuC{BcCw@w@cCaCyEsE_CqBcIeGoD_CcGwDyGsDqHaD{DyAmFqBoJwCkHeB}K}BoB[{Cg@mEe@iD[wFe@kAI_CMaBGqAEyAEoCG_GGqDC_A?mFAgN?yICwA?eD?cM?sJCq@?aAAeH?gD?mOE_JAkMA_E?kGA_O?eUGgWKiDA}Y?aDEeR?mSOqMIiGIsMOaO_@uGYkDOkBOmCQoCOkCUmBOeBOwAOaJcAwC[yDc@{B]yCc@}B]aFy@yAWsGmA{EeAyG{AeBa@sEiAoGgBcBe@yAa@}Ae@mAa@_EoAmIoCyEiBkAc@kD}Aw@[qD{AwB_AoB}@aEyBcBw@m@[YMcD_BgAk@kEaCwCcBwFcDeHaEoLiHqBmAqD_Ca`@eVuDcCcCyA}AcAkEoCsCqB{KwHcAs@_As@eJ{GsC}BuAkAwFqEgFyEaC{BaCiCsDsDyBeC_BiBoAsA}AoBqNoQgIyKoIwKyAsB_AiAwAqB_HaEwCgDcGkGqa@eh@iHiJwOgSmJ{MiG_IqIcLyPqR}CgEuBoCwAqB{BmCuC_CyCEeFUkEb@aEfA}DbC{FzFoF~EcFfDoCnA}EnBaOzDyXhUkPpNye@za@yBx@iBh@aFpF}A`BwQ`QcIrH_O|MsI|HoD`DmDtCaE|CsEjDaAt@qCzBmB|AsB~AgNrJePnIeQ`JsJ|EmJ|EoPrJoH|EYR{BvAkBjA_RbKqEjCwSzLoJrF{BjAkBbAaP|I{DxBuGfD{IbEaA`@qBx@aGpBcEfAyOpD{QrD{NvCqKrBsG|AgOnDsGjAci@nKyNvBeGx@iLfBKBiI~AwXpGkIlBeK|BcAT}NvCaStJuUrIyFjC_E~BkI`GoCrCqDbEoCtEaFfG_DxCiFbD_Al@kCnAcFzAqEXuESaHaAeEwAuCgBkFeCeIkE{G_BqMkAoGTaIjAwS`EaNrCeFtA{G~@qFz@cHpAeJ`BmDl@wCd@yBL}ANeDPcDJwAJqDTwHPwFNoN`@yCJiVF{D?eTBqC@qBAiBAoAC}CKkCCkBEaDWq^iDwBSki@eGcYyBiDYgCSwIYyMs@eXoAsa@_DwAViAVq@De@BmWI_FOoD?_CFcKz@i@LcAVkA`@wBz@cBdAyEnCyEtC}DdC{QxJ_DjBkEzBoDtBaGnDeKzFgFdD}HnEqHfEuIdFkQdKyGbEoFfDsFvDsDlCqEbGkCvCeC`DeAxAoAjC}AfDkA~C{IfUmH`YiArDeDdJd@bC`@lAlAtAxBhAzLjEjDhAvHjC~Bp@rEvAxErAtJkY`JcWf@oAlBwDXa@rAgA~EsCvFYjD{BOiCHqD|CaCxCyA_EuK]aAQ_BJcBf@mA`AaA`Ai@fPeIbUaL|DqB`@Il@b@Xb@@?","distance":15847.2,"duration":1382.5,"weight":1382.5,"legs":[{"distance":{"value":15847},"duration":{"value":1382},"steps":[]},{"distance":{"value":0},"duration":{"value":0},"steps":[]}]}],"country_code":"MY"}

      // })
    });
  }, [tasks, selectedTasks]);

  const checkBoxChange = (e: CheckboxChangeEvent) => {
    const taskId: any = e.target.id;
    const isChecked: Boolean = e.target.checked;
    setSelectedTasks({ [taskId]: isChecked });
  };

  return {
    selectedTasks,
    checkBoxChange,
  };
};

// ======================================================
//   Journey Information
// ======================================================

//   triggered when a journey is checked (radio)
const JourneySteppers = (props) => {
  const { t } = useTranslation();

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Delivery_Management",
  });

  const canUpdate = permission.rights.includes("UPDATE");

  const { tasks, selectedTasks, handleOpenEditRoute } = props || {};
  const { Step } = Steps;

  return (
    <div className="ct-journey">
      {tasks.map((trip, index) => {
        for (let x = 0; x < Object.keys(selectedTasks).length; x++) {
          if (
            selectedTasks.hasOwnProperty(trip.trainPlanCode) &&
            selectedTasks[trip.trainPlanCode] == true
          ) {
            let currentStation = 0;

            const allStations = [...trip?.train?.station];

            // if last station is less than start station, reorder
            if (trip?.lastStation < trip?.startStation) {
              allStations?.sort((a, b) => (a.stationCode > b.stationCode ? -1 : 1));
            }

            // set stepper current index
            for (let i = 0; i < allStations?.length; i++) {
              if (allStations[i]?.stationCode === trip?.lastKnownLocationData?.stationCode) {
                currentStation = i;
              }
            }

            return (
              <div className="ct-journey" key={index}>
                <Col span={24}>
                  <Row className="status-label" gutter={[16, 16]} align="bottom">
                    <Col flex="none">
                      <div className="journey-code">
                        <h3>{trip?.trainPlanCode}</h3>
                      </div>
                    </Col>
                    <Col flex="none">
                      <h4 className="status fit-content BOARDED">{trip?.status}</h4>
                    </Col>
                    <Col flex="auto"></Col>
                    {canUpdate && trip?.status !== "COMPLETED" && (
                      <Col flex="none">
                        <Button
                          span={4}
                          type="primary"
                          label={t("action.editRoute")}
                          size="large"
                          onClick={() => handleOpenEditRoute(trip)}
                        />
                      </Col>
                    )}
                  </Row>
                </Col>
                <Divider />
                <Col span={24}>
                  <div className="ct-journey-stepper">
                    <Steps progressDot current={currentStation}>
                      {allStations?.map((details, index) => (
                        <Step key={index} title={details?.name} />
                      ))}
                    </Steps>
                  </div>
                </Col>
              </div>
            );
          }
        }
      })}
    </div>
  );
};

// ======================================================
//   Main Component
// ======================================================
const ControlTowerMidMileView: React.FC<MidMileCTProps> = ({ midMileData, handleUpdateRoute }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const query = useQuery();

  const tabKey = "0";
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);
  const [forceRender, setForceRender] = useState<boolean>(false);
  const [tasks, setTasks] = useState<any>(midMileData);

  const { modalObject, modal, callModal, handleResetModalState } = useModalHandler();
  const { handleOpenEditRoute } = useMidMileView({ modalObject, handleUpdateRoute });
  const { selectedTasks, checkBoxChange } = useNextBillion({ tasks });

  const journeyProps = { tasks, selectedTasks, handleOpenEditRoute };

  let tabs = [
    {
      tabKey: "0",
      label: t("tab.all"),
      component: null,
    },
  ];

  tabs = !isRoleCustomer
    ? tabs.concat([
        {
          tabKey: "1",
          label: t("tab.ongoing"),
          component: null,
        },
        {
          tabKey: "2",
          label: t("tab.planned"),
          component: null,
        },
      ])
    : tabs;

  const handleTabClick = (key) => {
    setTabActiveKey(key);
    setForceRender(true);
  };

  useEffect(() => {
    if (tabActiveKey === "0") {
      setTasks(midMileData);
    } else if (tabActiveKey === "1") {
      let onGoingData: any = filter(midMileData, (midMileObj) => {
        return midMileObj.status == "ONGOING";
      });
      setTasks(onGoingData);
    } else if (tabActiveKey === "2") {
      let plannedData: any = filter(midMileData, (midMileObj) => {
        return midMileObj.status == "PLANNED";
      });
      setTasks(plannedData);
    }
  }, [tabActiveKey, midMileData]);

  return (
    <div>
      <div className="control-tower">
        <Row>
          <Col xs={24} sm={24} md={18} xl={16} xxl={18}>
            <div
              style={{
                width: "100%",
                minHeight: "500px",
                height: "auto",
                // position: 'fixed',
                // top: '0',
                // left: '0'
              }}
              id="map"
            ></div>
            {/*<img src="/assets/images/map-placeholder.png" className="map-placeholder"></img>*/}
          </Col>
          <Col xs={24} sm={24} md={6} xl={8} xxl={6} className="task-menu">
            <h2>Journeys</h2>
            <Row>
              <Col xs={12} sm={12} md={24} xl={24}>
                <Tabs
                  destroyInactiveTabPane={true}
                  onTabClick={handleTabClick}
                  defaultActiveKey="0"
                  activeKey={tabActiveKey}
                  tabBarGutter={25}
                  tabs={tabs}
                />
              </Col>
            </Row>
            <div className="right-bar">
              {tasks.length ? (
                map(tasks, (task, index) => {
                  return (
                    <Row key={index}>
                      <Col
                        xs={16}
                        sm={16}
                        md={16}
                        xl={16}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Radio
                          disabled={false}
                          checked={selectedTasks[task.trainPlanCode]}
                          name="diolkoServicePoints"
                          onChange={checkBoxChange}
                          style={{ marginRight: 10 }}
                          id={task.trainPlanCode}
                        ></Radio>
                        <span
                          className="checkbox-marker"
                          style={{ marginLeft: "25px", background: "#E8A51A" }}
                        ></span>
                        <span style={{ marginLeft: "20px" }}>{task.trainPlanCode || "-"}</span>
                      </Col>
                      <Col xs={8} sm={8} md={8} xl={8} className="driver">
                        {task.plateNo}
                      </Col>
                    </Row>
                  );
                })
              ) : (
                <></>
              )}
            </div>
          </Col>
        </Row>
        {!isEmpty(selectedTasks) && <JourneySteppers {...journeyProps} />}
        <BasicModal
          className="modal"
          isOpen={modal.open}
          isClosable={true}
          isMaskClosable={false}
          onClose={() => {
            handleResetModalState();
          }}
          width={"50vw"}
        >
          {callModal}
        </BasicModal>
      </div>
    </div>
  );
};

export default ControlTowerMidMileView;
